import React, { useRef } from "react";
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
} from "@hello-pangea/dnd";
import { useDragContext } from "../../util/DragProvider";
import { createPortal } from "react-dom";

export interface DragListItemProps<T> {
  /**
   * Unique identifier for the draggable item.
   * Must be unique within the drag context.
   */
  id: string;
  /**
   * The position index of the item within the list.
   * Used to determine order and track position changes.
   */
  index: number;
  /**
   * Optional data to associate with this draggable item.
   * This data is passed through drag events for handling drops.
   */
  data?: T;
  /**
   * CSS class name(s) applied to the portal element when dragging.
   * The item is rendered in a portal during drag for proper z-index layering.
   */
  portalClassname?: string;
  /**
   * Content to render for the item.
   * Can be a React node or a render function that receives drag state.
   * The render function provides handleProps (for custom drag handles) and isDragging state.
   */
  children:
    | React.ReactNode
    | ((args: {
        handleProps?: DraggableProvided["dragHandleProps"];
        isDragging: boolean;
      }) => React.ReactNode);
}

/**
 * Individual draggable item component for use within DragList.
 * Wraps content to make it draggable and handles portal rendering during drag.
 * Supports both simple children and render function patterns for advanced use cases.
 */
export function DragListItem<T = unknown>({
  id,
  index,
  data,
  children,
  portalClassname = "",
}: DragListItemProps<T>) {
  const { setDragData } = useDragContext();
  const wasDragging = useRef(false);

  return (
    <Draggable draggableId={id} index={index}>
      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
        if (snapshot.isDragging && !wasDragging.current) {
          setDragData(data || id);
        }
        wasDragging.current = snapshot.isDragging;

        if (!snapshot.isDragging)
          return (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              className="min-w-0"
            >
              {typeof children === "function"
                ? children({
                    handleProps: provided.dragHandleProps,
                    isDragging: snapshot.isDragging,
                  })
                : children}
            </div>
          );

        return createPortal(
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={portalClassname}
          >
            {typeof children === "function"
              ? children({
                  handleProps: provided.dragHandleProps,
                  isDragging: snapshot.isDragging,
                })
              : children}
          </div>,
          document.body
        );
      }}
    </Draggable>
  );
}

DragListItem.displayName = "DragList::Item";
